import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import './Home.css';

// --- Background Images ---
const images = [
  'https://images.unsplash.com/photo-1575237402880-4b496a83ae04?q=80&w=1374&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?auto=format&fit=crop&w=1350&q=80',
  'https://images.unsplash.com/photo-1519985176271-adb1088fa94c?auto=format&fit=crop&w=1350&q=80',
  'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1350&q=80',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1350&q=80'
];

// --- City Data ---
const cities = [
  {
    id: 1,
    name: 'Nairobi',
    image: 'https://images.unsplash.com/photo-1585523658894-cc78fc2c8f67?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fG5haXJvYml8ZW58MHx8MHx8fDA%3D',
    description: 'The Green City in the Sun.',
    details: {
      enjoyment: 'Experience a unique blend of urban energy and wildlife. Visit the Giraffe Centre or adopt an elephant at Sheldrick Wildlife Trust.',
      thrills: 'The only capital city with a National Park within its borders. Go on a safari game drive against a backdrop of skyscrapers.',
      culture: 'Visit the Maasai Market for colorful crafts and beadwork, and explore the National Museum for a deep dive into Kenyan history.'
    }
  },
  {
    id: 2,
    name: 'Dar es Salaam',
    image: 'https://images.unsplash.com/photo-1605713288610-00c1c630ca1e?auto=format&fit=crop&w=800&q=80',
    description: 'The Haven of Peace.',
    details: {
      enjoyment: 'Relax on the pristine beaches of Coco Beach or take a short ferry ride to the exotic Mbudya Island for fresh seafood.',
      thrills: 'Explore the chaotic and vibrant Kariakoo Market, or take a high-speed ferry to Zanzibar for a weekend getaway.',
      culture: 'Immerse yourself in Swahili coastal culture, enjoy Taarab music, and taste the incredible fusion of African, Arab, and Indian cuisines.'
    }
  },
  {
    id: 3,
    name: 'Kampala',
    image: 'https://images.unsplash.com/photo-1675756261486-09bd1e0f6c8a?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    description: 'The City of Seven Hills.',
    details: {
      enjoyment: 'Known as the nightlife capital of East Africa. The energy in bars and lounges in Kololo and Kabalagala is unmatched.',
      thrills: 'Take a boda-boda tour through the bustling streets or visit the Kasubi Tombs.',
      culture: 'Visit the Ndere Cultural Centre for mesmerizing traditional dance performances from all over Uganda.'
    }
  },
  {
    id: 4,
    name: 'Zanzibar',
    image: 'https://images.unsplash.com/photo-1711091704998-3bea86948c83?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    description: 'Clean, Green, and Beautiful.',
    details: {
      enjoyment: 'Unwind on the sugar-white sands of Nungwi Beach, sipping fresh coconut water as traditional Dhows sail across the golden horizon at sunset.',
      thrills: 'Ride the waves to the historic Prison Island to encounter ancient giant tortoises, then dive into the crystal-clear abyss of the Nakupenda Sandbank for a snorkeling adventure.',
      culture: 'Embark on a sensory journey through a traditional spice farm to smell vanilla and cloves, then explore the Sultans Palace to uncover the islands royal past.'
    }
  },
  {
    id: 5,
    name: 'Johannesburg',
    image: 'https://plus.unsplash.com/premium_photo-1742418133861-931394d179da?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8am9oYW5uZXNidXJnfGVufDB8fDB8fHww',
    description: 'The City of Gold.',
    details: {
      enjoyment: 'Explore Maboneng Precinct for trendy art galleries, markets, and rooftop bars.',
      thrills: 'Visit Gold Reef City for rollercoasters and underground mine tours.',
      culture: 'Walk down Vilakazi Street in Soweto, the only street in the world to house two Nobel Prize winners.'
    }
  }
];

const phrase = "Embark on a journey to the world's most breathtaking destinations, discover hidden gems, and create memories that will last a lifetime.";

// Fade Animation Variants
const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

function Home() {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [selectedCity, setSelectedCity] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { isAuthenticated } = useAuth();
  const typingSpeed = 120;
  const deletingSpeed = 60;
  const delay = 2000;

  // Manual Navigation Functions
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Auto-cycle timer (clears when you interact manually to prevent jumps)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleTyping = () => {
      const fullText = phrase;
      setText(isDeleting ? fullText.substring(0, text.length - 1) : fullText.substring(0, text.length + 1));
      if (!isDeleting && text === fullText) setTimeout(() => setIsDeleting(true), delay);
      else if (isDeleting && text === '') { setIsDeleting(false); setLoopNum(loopNum + 1); }
    };
    const timer = setTimeout(handleTyping, isDeleting ? deletingSpeed : typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum]);

  return (
    <div className="home-container">
       

      {/* --- HERO SECTION --- */}
      <div className="hero-section">
        
        {/* Fading Background Images */}
        <div className="background-container">
          <AnimatePresence mode='popLayout'>
            <motion.div
              key={currentImageIndex}
              className="fade-image-item"
              style={{ backgroundImage: `url(${images[currentImageIndex]})` }}
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 5.5 }}
            />
          </AnimatePresence>
        </div>

        {/* --- NEW: Left Arrow (<) --- */}
        <button className="slider-arrow left-arrow" onClick={prevImage}>
          &#60;
        </button>

        {/* --- NEW: Right Arrow (>) --- */}
        <button className="slider-arrow right-arrow" onClick={nextImage}>
          &#62;
        </button>

        {/* Text Content */}
        <div className="home-content">
          <h1>Adventure Awaits with <br/>Countryside Tours and Travel</h1>
          <p>{text}<span className="cursor" /></p>
          {!isAuthenticated && (
              <div className="auth-section">
                <Link to="/login" className="login-button">Login</Link>
                <p className="create-account-text">Don't have an account? <Link to="/login" className="create-account-link">Create one here</Link></p>
              </div>
          )}
        </div>

        {/* --- NEW: Dots Indicator --- */}
        <div className="slider-dots">
          {images.map((_, index) => (
            <span 
              key={index} 
              className={`dot ${index === currentImageIndex ? 'active' : ''}`}
              onClick={() => goToImage(index)}
            ></span>
          ))}
        </div>

      </div>

      {/* Top Cities Section */}
      <div className="cities-section">
        <h2 className="section-title">Top Cities</h2>
        <div className="cities-grid">
          {cities.map((city) => (
            <div key={city.id} className="city-card" onClick={() => setSelectedCity(city)}>
              <div className="city-image" style={{ backgroundImage: `url(${city.image})` }}></div>
              <div className="city-info-overlay">
                <h3>{city.name}</h3>
                <p>{city.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for Details */}
      <AnimatePresence>
        {selectedCity && (
          <motion.div 
            className="modal-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelectedCity(null)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-modal" onClick={() => setSelectedCity(null)}>×</button>
              <h2>Explore {selectedCity.name}</h2>
              <div className="modal-body">
                <div className="modal-section">
                  <h4>🌟 Enjoyment</h4>
                  <p>{selectedCity.details.enjoyment}</p>
                </div>
                <div className="modal-section">
                  <h4>🔥 Thrills</h4>
                  <p>{selectedCity.details.thrills}</p>
                </div>
                <div className="modal-section">
                  <h4>🎭 Culture</h4>
                  <p>{selectedCity.details.culture}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="info-section">
        <div className="info-overlay">
          <h2>Unlock the Full Experience</h2>
          <p>
            Your journey doesn't stop at browsing. <strong>Log in</strong> to book your dream tour, 
            make direct inquiries, and share your own stories. 
            From discovering hidden gems to leaving comments about your 
            unforgettable experiences, everything you need is just a click away.
          </p>
          {!isAuthenticated && (
            <Link to="/login" className="info-btn">
              Join the Adventure
            </Link>
          )}
        </div>
      </div>
      
      <Footer/>
    </div>
  );
}

export default Home;